import { useState } from "react";
import { ShieldCheck, ArrowRight, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/utils/api";
import { AuthField, AuthSubmit } from "@/components/auth/AuthField";

export const MfaLoginStep = ({ mfaToken, onCancel }) => {
  const { verifyMfa } = useAuth();
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await verifyMfa(mfaToken, code.trim()); }
    catch (err) {
      if (err?.response?.status === 401) { onCancel(); return; }
      setError(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="mfa-login-step">
      <div className="mb-5 flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
        <ShieldCheck size={18} className="mt-0.5 shrink-0" />
        <p>{useRecovery ? "Enter one of your saved recovery codes. Each code works once." : "Open Microsoft Authenticator and enter the 6-digit code for this account."}</p>
      </div>
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="mfa-login-error">{error}</div>}
      <form onSubmit={submit} className="space-y-5">
        <AuthField
          label={useRecovery ? "Recovery code" : "Authentication code"} icon={KeyRound} value={code} onChange={(e) => setCode(e.target.value)}
          placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"} required autoFocus autoComplete="one-time-code"
          inputMode={useRecovery ? "text" : "numeric"} maxLength={useRecovery ? 11 : 6} testId="mfa-code-input"
        />
        <AuthSubmit loading={loading} disabled={code.trim().length < 6} testId="mfa-verify-btn">
          {loading ? "Checking..." : <>Verify <ArrowRight size={16} /></>}
        </AuthSubmit>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs">
        <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }} className="font-medium text-blue-600 hover:underline" data-testid="mfa-toggle-recovery">
          {useRecovery ? "Use authenticator code instead" : "Lost your phone? Use a recovery code"}
        </button>
        <button type="button" onClick={onCancel} className="text-slate-500 hover:underline" data-testid="mfa-cancel-btn">Back to sign in</button>
      </div>
    </div>
  );
};
